import { Box, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';

import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import { useGetSubjectsByProfileIdQuery } from '@verantos-dev/data-access-subjects';
import { wrapCellStyle } from '@verantos-dev/shared-components';
import { type CohortSubjectDetail } from '@verantos-dev/types-cohorts';

export const useCohortSubjectList = (projectId: string, cohortId: string) => {
  const intl = useIntl();
  const theme = useTheme();

  const { data: cohortData } = useCohortBuilderData();
  const latestCohortStatus = cohortData?.data?.latestStatus;

  const useFetchSubjectListData = (searchParams?: string) => {
    // transform searchParams to urlParams
    // search params page, pageSize -> page, perPage e.g page=1&pageSize=10
    // sort --> sort e.g. sort=personId:asc

    const urlParams = searchParams?.replace(/pageTab=[^&]*&?/, '') || '';
    return useGetSubjectsByProfileIdQuery({
      projectId,
      cohortProfileId: cohortId,
      urlParams,
    });
  };

  const { data: subjectsData, error: subjectsError } = useFetchSubjectListData();

  const columnDefinition: GridColDef<CohortSubjectDetail>[] = [
    {
      headerName: intl.formatMessage({
        id: 'projects.cohortBuilder.subjectsColumnHeader.personId',
      }),
      type: 'string',
      field: 'subjectId',
      minWidth: 300,
      flex: 1.5,
      filterable: false,
      sortable: false,
    },
    {
      headerName: intl.formatMessage({ id: 'projects.cohortBuilder.subjectsColumnHeader.age' }),
      type: 'number',
      field: 'ageAtIndex',
      minWidth: 130,
      maxWidth: 130,
      flex: 0,
      filterable: false,
      sortable: false,
    },
    {
      headerName: intl.formatMessage({ id: 'projects.cohortBuilder.subjectsColumnHeader.gender' }),
      type: 'string',
      field: 'gender',
      minWidth: 150,
      flex: 1,
      filterable: false,
      renderCell: (params) => {
        const gender = params?.row.gender;
        return <Box sx={wrapCellStyle}>{gender}</Box>;
      },
    },
    {
      headerName: intl.formatMessage({ id: 'projects.cohortBuilder.subjectsColumnHeader.race' }),
      type: 'string',
      field: 'race',
      minWidth: 200,
      flex: 3,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const race = params?.row.race;
        return <Box sx={wrapCellStyle}>{race}</Box>;
      },
    },
    {
      headerName: intl.formatMessage({
        id: 'projects.cohortBuilder.subjectsColumnHeader.ethnicity',
      }),
      type: 'string',
      field: 'personEthnicity',
      minWidth: 200,
      flex: 3,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const ethnicity = params?.row.ethnicity;
        return <Box sx={wrapCellStyle}>{ethnicity}</Box>;
      },
    },
  ];

  const subjectWorkflowMessages = {
    draftTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortSubjectsPageTab.draft.title',
    }),
    draftMessage: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortSubjectsPageTab.draft.message',
    }),
    processedNoDataTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortSubjectsPageTab.processedNoData.title',
    }),
    processedNoDataMessage: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortSubjectsPageTab.processedNoData.message',
    }),
    processingTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortSubjectsPageTab.processing.title',
    }),
    processingMessage: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortSubjectsPageTab.processing.message',
    }),
    loadingTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.loading.title',
    }),
  };

  return {
    theme,
    latestCohortStatus,
    useFetchSubjectListData,
    columnDefinition,
    subjectsData,
    subjectsError,
    subjectWorkflowMessages,
  };
};
